import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MdDialog, MdDialogRef } from '@angular/material';

import { ConstantsService } from 'app/services/constants-service';
import { CurrencyService } from 'app/shared/services/currency-service';
import { FormErrors} from 'app/shared/validators/generic-validators';
import { MessageService } from 'app/services/messages.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { TrackingConstants } from 'app/shared/services/tracking/tracking-constants.service';
import { TrackingService } from 'app/shared/services/tracking/google-analytics-events.service';

import { IntegrationModel } from 'app/shared/models/integration.model';
import { GenericSearchDetails } from 'app/shared/models/generic-search-details.model';

import { FinanceService } from 'app/finance/finance.service';
import { IntegrationService } from './../integration.service';

@Component({
    selector: 'integration',
    templateUrl: './integration.component.html',
})

/**
 * Component for adding/updating an Integration.
 */
export class IntegrationComponent implements OnInit {
    integrationForm: FormGroup;
    savingIntegration: boolean;
    selectedIntegration: IntegrationModel;
    integrationId: number;
    clientId: number;
    clientAccountNumbers: string[];
    currencies: string[];
    action: string;

    integrationClientSearchURI = ConstantsService.CLIENT_SEARCH;
    searchPlaceholder = MessageService.CLIENT_NAME_SEARCH_PLACEHOLDER;

    formErrors = {
        'clientName': '',
        'clientAccountNo': '',
        'extAccId': '',
        'extAccName': '',
        'currency': '',
        'type': '',
    };

    validationMessages = {
        'clientName': {
            'required': 'Required field value.',
            'invalid': 'Invalid client name.',
        },
        'clientAccountNo': {
            'required': 'Required field value.',
        },
        'extAccId': {
            'required': 'Required field value.',
        },
        'extAccName': {
            'required': 'Required field value.',
        },
        'currency': {
            'required': 'Required field.',
        },
        'type': {
            'required': 'Required field.',
        },
    };

    constructor(
        public dialogRef: MdDialogRef<any>,
        private formBuilder: FormBuilder,
        private currencyService: CurrencyService,
        private financeService: FinanceService,
        private integrationService: IntegrationService,
        private snackBarService: SnackBarService,
        private trackingService: TrackingService) { }

    createForm() {
        this.integrationForm = this.formBuilder.group({
            clientName: ['', Validators.required],
            clientAccountNo: ['', Validators.required],
            extAccId: ['', Validators.required],
            extAccName: ['', Validators.required],
            currency: ['', Validators.required],
            type: ['', Validators.required],
        });

        this.integrationForm.valueChanges.subscribe(data => this.onValueChanged(data));
        this.onValueChanged();
    }

    callSnackBarService(response: any, successMsg: string, failureMsg: string) {
        this.snackBarService.showStatus(response, successMsg, failureMsg);
    }

    onValueChanged(data?: any) {
        if (!this.integrationForm) {
            return;
        }
        FormErrors('integrationForm', this);
    }

    ngOnInit() {
        this.createForm();
        this.setCurrencies();
        this.updateIntegrationForm();
    }

    onClientNameChanged(value){
        this.clientAccountNumbers = [];
        this.integrationForm.controls['clientAccountNo'].setValue(null);
        let nameControl = this.integrationForm.controls['clientName'];
        if (!!value && typeof value === 'string' && value != nameControl.value && nameControl.touched) {
            this.clientId = null;
            nameControl.setErrors({ 'invalid': this.validationMessages['clientName']['invalid']});
            nameControl.markAsTouched();
        } else if (!value) {
            nameControl.markAsUntouched();
        }
    }

    onClientSelectionEvent(searchDetails: GenericSearchDetails) {
        this.clientId = searchDetails.clientId;
        this.integrationForm.controls['clientName'].setValue(searchDetails.clientName);
        this.integrationForm.controls['clientName'].markAsTouched();
        this.financeService.getBankAccountsForEntity(searchDetails.entityId)
            .subscribe(response => {
                if (response.message) {
                    this.callSnackBarService(response, '', '');
                 } else {
                     this.clientAccountNumbers = response.map(a => a.accountNumber);
                 }
            });
    }

    onSubmit({ value, valid }) {
        const msgSuccess = this.action == 'Add' ? MessageService.ADD_INTEGRATION_SUCCESS : MessageService.EDIT_INTEGRATION_SUCCESS;
        const msgFailure = this.action == 'Add' ? MessageService.ADD_INTEGRATION_FAILURE : MessageService.EDIT_INTEGRATION_FAILURE;
        const integrationModel = new IntegrationModel();
        integrationModel.id = this.integrationId;
        integrationModel.clientId = this.clientId;
        integrationModel.clientAccountNo = value.clientAccountNo;
        integrationModel.extAccId = value.extAccId;
        integrationModel.extAccName = value.extAccName;
        integrationModel.currency = value.currency;
        integrationModel.type = value.type;
        this.savingIntegration = true;
        this.integrationService.saveIntegration(integrationModel)
            .subscribe(response => {
                this.savingIntegration = false;
                if (response.flagResult) {
                    this.dialogRef.close(response);
                }
                this.trackingService.emitEvent(
                    TrackingConstants.INTEGRATIONS,
                    TrackingConstants.SAVE_INTEGRATION,
                    TrackingConstants.SUCCESS, 1);
                this.callSnackBarService(response, msgSuccess, msgFailure);
            },
            err => {
                this.savingIntegration = false;
                this.trackingService.emitEvent(
                    TrackingConstants.INTEGRATIONS,
                    TrackingConstants.SAVE_INTEGRATION,
                    TrackingConstants.FAILURE, 1);
                this.callSnackBarService(err, msgSuccess, msgFailure);
            });
    }

    private setCurrencies() {
        this.currencyService.getCurrencies()
            .subscribe(currencies => {
                this.currencies = currencies.arrResult;
            });
    }

    updateIntegrationForm(): void {
        if (this.selectedIntegration) {
            for (let inner in this.integrationForm.controls) {
                this.integrationForm.get(inner).markAsTouched();
                this.integrationForm.get(inner).updateValueAndValidity();
            }
            this.integrationId = this.selectedIntegration.id;
            this.clientId = this.selectedIntegration.clientId;
            this.clientAccountNumbers = this.selectedIntegration.clientAccounts;
            this.integrationForm.patchValue({
                id: this.selectedIntegration.id,
                clientName: this.selectedIntegration.clientName,
                clientAccountNo: this.selectedIntegration.clientAccountNo,
                extAccId: this.selectedIntegration.extAccId,
                extAccName: this.selectedIntegration.extAccName,
                currency: this.selectedIntegration.currency,
                status: this.selectedIntegration.status,
                type: this.selectedIntegration.type
            });
        }
    }
}


// WEBPACK FOOTER //
// ./src/app/admin/integrations/integration/integration.component.ts